import { FC, useMemo, useState } from 'react';
import {
  ArrayField,
  BooleanField,
  Datagrid,
  DeleteWithConfirmButton,
  Labeled,
  ListContextProvider,
  RaRecord,
  RecordContextProvider,
  ReferenceArrayField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  useGetMany,
  useList,
  useListContext,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { Box, Chip, Divider, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { ChannelRelease, CurrentRelease, HardwareRevisionRef, UpdateChannel } from 'types/UpdateChannel';
import DialogContainer from 'components/DialogContainer';
import { FormattedDateField } from 'components/FormattedDateField';
import AddSubItem from 'components/DialogContainer/AddSubItem';
import EditSubItem from 'components/DialogContainer/EditSubItem';
import { AddSoftwareReleaseForm } from './components/AddSoftwareReleaseForm';
import { EditSoftwareReleaseForm } from './components/EditSoftwareReleaseForm';
import { UpdateChannelUnitsTab } from './components/UpdateChannelUnitsTab';

type HardwareNameMap = Record<number, string>;

const toggleGroupSx = {
  'flexWrap': 'wrap',
  'gap': 1,
  '& .MuiToggleButtonGroup-grouped': {
    'border': '1px solid',
    'borderColor': 'primary.main',
    'borderRadius': 1,
    'color': 'secondary.main',
    'textTransform': 'none',
    '&:not(:first-of-type)': {
      borderLeft: '1px solid',
      borderLeftColor: 'primary.main',
    },
    '&.Mui-selected': {
      'backgroundColor': 'primary.main',
      'color': 'secondary.main',
      '&:hover': {
        backgroundColor: 'primary.main',
      },
    },
  },
} as const;

const useHardwareNames = (hardwareIds: number[]): HardwareNameMap => {
  const { data } = useGetMany('hardware', { ids: hardwareIds }, { enabled: hardwareIds.length > 0 });
  return useMemo(() => {
    const map: HardwareNameMap = {};
    (data ?? []).forEach((hardware) => {
      map[hardware.id as number] = (hardware.name as string) ?? String(hardware.id);
    });
    return map;
  }, [data]);
};

const RecordActionButtons = () => {
  const record = useRecordContext();
  const released_at: number | null = record?.released_at ? Date.parse(record?.released_at) : null;
  const now = Date.now();
  const t = useTranslate();
  return (
    <Box display="flex" justifyContent="center" p={2}>
      {released_at === null || released_at > now ? <EditSubItem resource="software_releases" /> : null}
      {released_at === null || released_at > now ? (
        <DeleteWithConfirmButton mutationMode="optimistic" redirect={false} />
      ) : null}
      {released_at && released_at <= now ? (
        <Typography variant="body2">{t('resources.channel_releases.action.released')}</Typography>
      ) : null}
    </Box>
  );
};

const CurrentReleaseName: FC<{ label?: string }> = () => {
  const release = useRecordContext<CurrentRelease>();
  if (release?.software_release_id) {
    return (
      <ReferenceField source="software_release_id" reference="software_releases" link={false}>
        <TextField source="name" />
      </ReferenceField>
    );
  }
  return <Typography variant="body2">{release?.name ?? '—'}</Typography>;
};

interface HardwareRevisionsSummaryProps {
  revisions: HardwareRevisionRef[];
  hardwareNames: HardwareNameMap;
}

const HardwareRevisionsSummary: FC<HardwareRevisionsSummaryProps> = ({ revisions, hardwareNames }) => {
  const translate = useTranslate();

  const hardwareIds = useMemo(
    () => Array.from(new Set(revisions.map((revision) => revision.hardware_id))),
    [revisions],
  );

  // Back-compat: fall back to the original single hardware_revision_id / hardware_id display.
  if (revisions.length === 0) {
    return (
      <>
        <Grid item xs={6} md={3}>
          <ReferenceField source="hardware_revision_id" reference="hardware_revisions">
            <Labeled>
              <TextField source="name" />
            </Labeled>
          </ReferenceField>
        </Grid>
        <Grid item xs={6} md={3}>
          <ReferenceField source="hardware_revision_id" reference="hardware_revisions" link={false}>
            <Labeled>
              <ReferenceField source="hardware_id" reference="hardware" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </ReferenceField>
        </Grid>
      </>
    );
  }

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {translate('resources.update_channels.section.hardware_revisions')}
      </Typography>
      <Stack spacing={1.5}>
        {hardwareIds.map((hardwareId) => (
          <Box
            key={hardwareId}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              gap: { xs: 0.5, sm: 2 },
            }}
          >
            <Typography variant="body2" fontWeight="bold" sx={{ minWidth: { sm: 120 } }}>
              {hardwareNames[hardwareId] ?? hardwareId}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {revisions
                .filter((revision) => revision.hardware_id === hardwareId)
                .map((revision) => (
                  <Chip
                    key={revision.hardware_revision_id}
                    size="small"
                    variant={revision.is_default ? 'filled' : 'outlined'}
                    color={revision.is_default ? 'primary' : 'default'}
                    label={revision.name}
                  />
                ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Grid>
  );
};

interface CurrentReleaseFiltersProps {
  revisions: HardwareRevisionRef[];
  hardwareNames: HardwareNameMap;
  selectedHardwareIds: number[];
  selectedRevisionIds: number[];
  onHardwareChange: (ids: number[]) => void;
  onRevisionChange: (ids: number[]) => void;
}

const CurrentReleaseFilters: FC<CurrentReleaseFiltersProps> = ({
  revisions,
  hardwareNames,
  selectedHardwareIds,
  selectedRevisionIds,
  onHardwareChange,
  onRevisionChange,
}) => {
  const distinctHardwareIds = useMemo(
    () => Array.from(new Set(revisions.map((revision) => revision.hardware_id))),
    [revisions],
  );
  // Only build a revision group for a selected hardware that actually has revisions.
  // Revision names are often duplicated (e.g. "default") across hardwares, so each
  // group is disambiguated by a hardware-name heading rather than a prefixed label.
  const revisionGroups = useMemo(
    () =>
      selectedHardwareIds
        .map((hardwareId) => ({
          hardwareId,
          hardwareRevisions: revisions.filter((revision) => revision.hardware_id === hardwareId),
        }))
        .filter((group) => group.hardwareRevisions.length > 0),
    [revisions, selectedHardwareIds],
  );

  if (distinctHardwareIds.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <ToggleButtonGroup
        value={selectedHardwareIds}
        onChange={(_event, value: number[]) => onHardwareChange(value)}
        size="small"
        sx={toggleGroupSx}
      >
        {distinctHardwareIds.map((hardwareId) => (
          <ToggleButton key={hardwareId} value={hardwareId}>
            {hardwareNames[hardwareId] ?? hardwareId}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {revisionGroups.map(({ hardwareId, hardwareRevisions }) => {
        const groupRevIds = hardwareRevisions.map((revision) => revision.hardware_revision_id);
        const groupValue = selectedRevisionIds.filter((id) => groupRevIds.includes(id));
        return (
          <Box
            key={hardwareId}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              gap: { xs: 0.5, sm: 2 },
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: { sm: 120 } }}>
              {hardwareNames[hardwareId] ?? hardwareId}
            </Typography>
            <ToggleButtonGroup
              value={groupValue}
              onChange={(_event, newGroupValue: number[]) =>
                // Merge: replace only this group's selections, preserving other groups'.
                onRevisionChange([...selectedRevisionIds.filter((id) => !groupRevIds.includes(id)), ...newGroupValue])
              }
              size="small"
              sx={toggleGroupSx}
            >
              {hardwareRevisions.map((revision) => (
                <ToggleButton key={revision.hardware_revision_id} value={revision.hardware_revision_id}>
                  {revision.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        );
      })}
    </Stack>
  );
};

interface CurrentReleaseSectionProps {
  revisions: HardwareRevisionRef[];
  hardwareNames: HardwareNameMap;
  selectedHardwareIds: number[];
  selectedRevisionIds: number[];
  onHardwareChange: (ids: number[]) => void;
  onRevisionChange: (ids: number[]) => void;
}

const CurrentReleaseSection: FC<CurrentReleaseSectionProps> = ({
  revisions,
  hardwareNames,
  selectedHardwareIds,
  selectedRevisionIds,
  onHardwareChange,
  onRevisionChange,
}) => {
  const translate = useTranslate();
  const record = useRecordContext<UpdateChannel>();
  const currentReleases = record?.current_releases;
  const hasCurrentReleases = Array.isArray(currentReleases) && currentReleases.length > 0;

  const filteredReleases = useMemo(() => {
    if (!currentReleases) return [];
    const noSelection = selectedHardwareIds.length === 0 && selectedRevisionIds.length === 0;
    if (noSelection) return currentReleases;
    return currentReleases.filter(
      (release) =>
        (selectedHardwareIds.length === 0 || selectedHardwareIds.includes(release.hardware_id)) &&
        (selectedRevisionIds.length === 0 || selectedRevisionIds.includes(release.hardware_revision_id)),
    );
  }, [currentReleases, selectedHardwareIds, selectedRevisionIds]);

  return (
    <>
      <Typography variant="h6">{translate('resources.update_channels.section.current_release')}</Typography>
      {hasCurrentReleases ? (
        <>
          <CurrentReleaseFilters
            revisions={revisions}
            hardwareNames={hardwareNames}
            selectedHardwareIds={selectedHardwareIds}
            selectedRevisionIds={selectedRevisionIds}
            onHardwareChange={onHardwareChange}
            onRevisionChange={onRevisionChange}
          />
          <RecordContextProvider value={{ ...record, current_releases: filteredReleases }}>
            <ArrayField source="current_releases">
              <Datagrid bulkActionButtons={false} sx={{ pointerEvents: 'none' }}>
                <ReferenceField
                  source="hardware_revision_id"
                  reference="hardware_revisions"
                  link={false}
                  label="resources.update_channels.fields.hardware_id"
                >
                  <ReferenceField source="hardware_id" reference="hardware" link={false}>
                    <TextField source="name" />
                  </ReferenceField>
                </ReferenceField>
                <ReferenceField
                  source="hardware_revision_id"
                  reference="hardware_revisions"
                  link={false}
                  label="resources.update_channels.fields.hardware_revision.name"
                >
                  <TextField source="name" />
                </ReferenceField>
                <CurrentReleaseName label="resources.update_channels.fields.software_release_id" />
              </Datagrid>
            </ArrayField>
          </RecordContextProvider>
        </>
      ) : (
        <ReferenceField source="software_release_id" reference="software_releases" link={false}>
          <Grid container spacing={6} mb={2}>
            <Grid item>
              <Labeled>
                <TextField source="name" component="strong" />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="software_components">
                <ArrayField source="software_components">
                  <Datagrid bulkActionButtons={false} sx={{ pointerEvents: 'none' }}>
                    <TextField source="name" label="Component" />
                    <ReferenceField source="software_version_id" reference="software_versions">
                      <TextField source="name" />
                    </ReferenceField>
                  </Datagrid>
                </ArrayField>
              </Labeled>
            </Grid>
          </Grid>
        </ReferenceField>
      )}
    </>
  );
};

type ReleaseRecord = RaRecord & Partial<ChannelRelease>;

const isUpcoming = (release: ReleaseRecord, now: number): boolean => {
  const releasedAt = release.released_at ? Date.parse(release.released_at) : null;
  return releasedAt === null || releasedAt > now;
};

const compareReleases = (a: ReleaseRecord, b: ReleaseRecord, now: number): number => {
  const upcomingA = isUpcoming(a, now);
  const upcomingB = isUpcoming(b, now);
  if (upcomingA !== upcomingB) return upcomingA ? -1 : 1;

  const timeA = a.released_at ? Date.parse(a.released_at) : null;
  const timeB = b.released_at ? Date.parse(b.released_at) : null;

  if (upcomingA) {
    // Not-yet-released: soonest first, nulls treated as furthest-future.
    return (timeA ?? Infinity) - (timeB ?? Infinity);
  }
  // Already released: newest first.
  return (timeB ?? 0) - (timeA ?? 0);
};

interface ReleaseHistoryListProps {
  selectedHardwareIds: number[];
  selectedRevisionIds: number[];
  currentReleaseKeys: Set<string>;
}

// Subtle "live/active" tint for the row(s) that are the channel's current release.
// Uses Primary Gold (#f1be48, see src/layout/theme.ts) at low opacity to stay readable.
const currentReleaseRowSx = { backgroundColor: 'rgba(241, 190, 72, 0.18)' } as const;

const ReleaseHistoryList: FC<ReleaseHistoryListProps> = ({
  selectedHardwareIds,
  selectedRevisionIds,
  currentReleaseKeys,
}) => {
  const translate = useTranslate();
  const { data } = useListContext<ReleaseRecord>();
  const hasSelection = selectedHardwareIds.length > 0 || selectedRevisionIds.length > 0;

  const visibleReleases = useMemo(() => {
    const releases = data ?? [];
    const now = Date.now();
    const matches = (release: ReleaseRecord): boolean => {
      // Back-compat: older API records without hardware fields match any selection.
      if (release.hardware_revision_id == null && release.hardware_id == null) return true;
      const hardwareMatch = release.hardware_id != null && selectedHardwareIds.includes(release.hardware_id);
      const revisionMatch =
        release.hardware_revision_id != null && selectedRevisionIds.includes(release.hardware_revision_id);
      return hardwareMatch || revisionMatch;
    };
    return releases.filter(matches).sort((a, b) => compareReleases(a, b, now));
  }, [data, selectedHardwareIds, selectedRevisionIds]);

  const listContext = useList({ data: visibleReleases });

  const rowSx = (release: ReleaseRecord) => {
    if (release.hardware_revision_id == null || release.software_release_id == null) return {};
    const key = `${release.hardware_revision_id}:${release.software_release_id}`;
    return currentReleaseKeys.has(key) ? currentReleaseRowSx : {};
  };

  return (
    <>
      <Box sx={{ pb: 2, display: 'flex', justifyContent: 'center' }}>
        <AddSubItem />
      </Box>
      {!hasSelection ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {translate('resources.update_channels.release_history.select_prompt')}
        </Typography>
      ) : (
        <ListContextProvider value={listContext}>
          <Datagrid bulkActionButtons={false} rowSx={rowSx}>
            <ReferenceField
              source="hardware_id"
              reference="hardware"
              link={false}
              label="resources.update_channels.fields.hardware_id"
            >
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField
              source="hardware_revision_id"
              reference="hardware_revisions"
              link={false}
              label="resources.update_channels.fields.hardware_revision.name"
            >
              <TextField source="name" />
            </ReferenceField>
            <TextField source="name" />
            <FormattedDateField source="released_at" />
            <RecordActionButtons />
          </Datagrid>
        </ListContextProvider>
      )}
    </>
  );
};

const UpdateChannelShowContent = () => {
  const translate = useTranslate();
  const record = useRecordContext<UpdateChannel>();

  const revisions = useMemo(() => record?.hardware_revisions ?? [], [record?.hardware_revisions]);
  const hardwareIds = useMemo(
    () => Array.from(new Set(revisions.map((revision) => revision.hardware_id))),
    [revisions],
  );
  const hardwareNames = useHardwareNames(hardwareIds);

  // Keys identifying the channel's current (live) release per revision, used to
  // highlight matching rows in the release-history Datagrid.
  const currentReleaseKeys = useMemo(() => {
    const keys = new Set<string>();
    (record?.current_releases ?? []).forEach((release) => {
      if (release.software_release_id != null) {
        keys.add(`${release.hardware_revision_id}:${release.software_release_id}`);
      }
    });
    return keys;
  }, [record?.current_releases]);

  const [selectedHardwareIds, setSelectedHardwareIds] = useState<number[]>([]);
  const [selectedRevisionIds, setSelectedRevisionIds] = useState<number[]>([]);

  const handleHardwareChange = (ids: number[]) => {
    setSelectedHardwareIds(ids);
    // Drop selected revisions whose hardware is no longer selected.
    setSelectedRevisionIds((previous) =>
      previous.filter((revisionId) => {
        const revision = revisions.find((item) => item.hardware_revision_id === revisionId);
        return revision ? ids.includes(revision.hardware_id) : false;
      }),
    );
  };

  return (
    <SimpleShowLayout>
      <Typography variant="h6">{translate('resources.update_channels.section.general')}</Typography>
      <Grid container spacing={6} mb={2}>
        <Grid item xs={6} md={3}>
          <Labeled>
            <TextField source="name" />
          </Labeled>
        </Grid>
        <Grid item xs={6} md={3}>
          <Labeled>
            <TextField source="short_name" />
          </Labeled>
        </Grid>
        <Grid item xs={6} md={3}>
          <Labeled>
            <BooleanField source="active" />
          </Labeled>
        </Grid>
        <HardwareRevisionsSummary revisions={revisions} hardwareNames={hardwareNames} />
      </Grid>

      <CurrentReleaseSection
        revisions={revisions}
        hardwareNames={hardwareNames}
        selectedHardwareIds={selectedHardwareIds}
        selectedRevisionIds={selectedRevisionIds}
        onHardwareChange={handleHardwareChange}
        onRevisionChange={setSelectedRevisionIds}
      />

      <Typography variant="h6">{translate('resources.update_channels.section.release_history')}</Typography>
      <DialogContainer
        createForm={<AddSoftwareReleaseForm />}
        editForm={<EditSoftwareReleaseForm />}
        resource="channel_releases"
      >
        <ReferenceArrayField source="channel_release_ids" reference="channel_releases">
          <ReleaseHistoryList
            selectedHardwareIds={selectedHardwareIds}
            selectedRevisionIds={selectedRevisionIds}
            currentReleaseKeys={currentReleaseKeys}
          />
        </ReferenceArrayField>
      </DialogContainer>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        {translate('resources.update_channels.section.units')}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {record && <UpdateChannelUnitsTab channelId={record.id as number} />}
    </SimpleShowLayout>
  );
};

export const UpdateChannelShow = () => (
  <Show>
    <UpdateChannelShowContent />
  </Show>
);
